import { Bell, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCommunityNotificationContext } from '../../../../Context/notificationContext';
import { useCookies } from 'react-cookie';
import IconCircle from '../../../atoms/avatars/IconCircle';
import './style.scss';
import { useEffect, useRef, useState } from 'react';
import { getData } from '../../../../services';

const NotificationCreate = () => {
  const { allNotifications , markAllAsRead , setMarkAllAsRead } = useCommunityNotificationContext();
  const [cookies] = useCookies(['euid', 'e_t']);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // State for displayed notifications
  const [displayedNotifications, setDisplayedNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createdDatee , setCreatedDate] = useState();

  // Function to fetch unread notifications
  const getUnreadNotifications = async () => {
    if (!cookies.euid || !cookies.e_t) return;
    
    setIsLoading(true);
    try {
      const res = await getData({
        endpoint: `Notification/employee/${cookies.euid}/unread`,
        params: { employeeId: cookies.euid },
        token: cookies.e_t,
      });
      
      if (res && res.data) {
       
        
        // Filter for current user and take top 5
        const userNotifications = res.data
          .filter(n => n.employeeId?.includes(cookies.euid))
          .slice(0, 4);
        
        setDisplayedNotifications(userNotifications);
        setHasUnread(userNotifications.length > 0);
      }
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load of unread notifications
  useEffect(() => {
    getUnreadNotifications();
  }, []);

  // Update displayed notifications when new notifications arrive via SignalR
  useEffect(() => {
    if (allNotifications && Array.isArray(allNotifications)) {
      // Merge real-time notifications with fetched ones
      // This ensures we keep any newly received notifications from SignalR
      setMarkAllAsRead(false);


      const latestNotifications = [...allNotifications]
        .filter(n => n.employees_to_notify?.includes(cookies.euid) && !n.isRead)
        .slice(0, 5);
      
      if (latestNotifications.length > 0) {
        setHasUnread(true);
        
        // Only update if we have new notifications to avoid unnecessary re-renders
        const existingIds = new Set(displayedNotifications.map(n => n.id));
        const hasNewNotifications = latestNotifications.some(n => !existingIds.has(n.id));
        
        if (hasNewNotifications) {
          setDisplayedNotifications(latestNotifications);
        }
      }

      setCreatedDate(allNotifications?.created_date);

    }
  }, [allNotifications, cookies.euid]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    
    // When opening the dropdown, refresh unread notifications
    if (!isOpen) {
      getUnreadNotifications();
    }
  };

  // Click outside handler
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="notification-container" ref={dropdownRef}>
      <div className="notification-bell" onClick={handleToggle}>
        <IconCircle
          svgIcon={<Bell size={13}/>}
          color={"#F8F9FB"}
          size={45}
          iconSize="13px"
          iconColor={"#8F95B2"}
          isSvg={true}
        />
        {hasUnread && !markAllAsRead ?  <div className="notification-indicator"></div> : null}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <button 
              onClick={() => {
                navigate('/dashboard/notifications');
                setIsOpen(false);
              }}
              className="view-all-btn"
            >
              View all
            </button>
          </div>
          
          <div className="notification-list">
            {isLoading ? (
              <div className="loading-notification"><Loader className="animate-spin" size={14} /></div>
            ) : !displayedNotifications || displayedNotifications.length === 0 ? (
              <div className="empty-notification">No notifications</div>
            ) : (
              displayedNotifications.map(notification => (
                <div 
                  key={notification.id}
                  className="notification-item"
                >
                  <div className="notification-content">
                    <div className="notification-text">
                      <p className="notification-message">
                        {notification.message}
                      </p>
                      <div className="notification-meta">
                        <span className="notification-time">
                          {formatDate(notification?.createdDate)}
                        </span>
                      </div>
                    </div>
                    <div className="unread-indicator"></div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCreate;